import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faVideo,
  faImage,
  faFileAlt,
  faCheckCircle,
  faLightbulb,
  faArrowRight,
  faPlay,
  faUsers,
  faStar,
  faClock,
  faBook,
  faChartLine,
  faHandshake,
  faGraduationCap,
  faPenToSquare,
  faBookOpen,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import './InstructorGuide.css';
import InstaTopbar from '../InstaTopbar/InstaTopbar';

const InstructorGuide = () => {
  const [activeSection, setActiveSection] = useState('getting-started');
  const navigate = useNavigate();

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: faGraduationCap },
    { id: 'course-creation', title: 'Course Creation', icon: faPlus },
    { id: 'content-creation', title: 'Content Creation', icon: faVideo },
    { id: 'best-practices', title: 'Best Practices', icon: faStar },
    { id: 'marketing', title: 'Marketing Tips', icon: faChartLine },
    { id: 'engagement', title: 'Student Engagement', icon: faUsers },
  ];

  const renderGettingStarted = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faGraduationCap} /> Getting Started as an Instructor</h2>

      <div className="guide-card">
        <h3>Welcome to Your Teaching Journey!</h3>
        <p>
          Creating your first course can feel big — but you don’t have to do everything at once.
          This guide explains the flow in simple steps, from planning → creating a draft → adding lessons → setting price → publishing.
        </p>
      </div>

      <div className="steps-container">
        <div className="step">
          <div className="step-number">1</div>
          <div className="step-content">
            <h4>Complete Your Profile</h4>
            <p>Add a clear photo, a short bio, and what you teach. Students trust instructors who look real and professional.</p>
            <Link to="/instructor-about" className="guide-link">
              <FontAwesomeIcon icon={faArrowRight} /> Edit Your Profile
            </Link>
          </div>
        </div>

        <div className="step">
          <div className="step-number">2</div>
          <div className="step-content">
            <h4>Understand Your Audience</h4>
            <p>Decide who this course is for (beginner/intermediate/advanced). This helps you choose the right language, examples, and lesson order.</p>
          </div>
        </div>

        <div className="step">
          <div className="step-number">3</div>
          <div className="step-content">
            <h4>Plan Your Course Structure</h4>
            <p>Write a simple outline: Sections → Lessons. Start from basics, then move step-by-step to advanced topics.</p>
          </div>
        </div>
        <div className="step">
          <div className="step-number">4</div>
          <div className="step-content">
            <h4>Track Your Performance</h4>
            <p>
              After you publish, use Performance Overview to see enrollments and course performance. It helps you understand what students like and what to improve.
            </p>
            <Link to="/performance" className="guide-link">
              <FontAwesomeIcon icon={faArrowRight} /> Performance Overview
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCourseCreation = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faPlus} /> Course Creation Process</h2>

      <div className="guide-card highlight">
        <h3>Ready to Create Your Course?</h3>
        <p>Click below to create a course draft. You can edit it later before publishing.</p>
        <Link to="/add-new-course" className="cta-button">
          <FontAwesomeIcon icon={faPlus} /> Create New Course
        </Link>
      </div>

      <div className="guide-card">
        <h3>Before you start (quick checklist)</h3>
        <ul>
          <li><strong>Course title:</strong> up to 150 characters</li>
          <li><strong>Short description:</strong> max 20 words</li>
          <li>
            <strong>Introduction video:</strong> MP4, AVI, MOV, WebM - <strong>maximum 5 minutes</strong>, recommended 720p+.
            <br />
            <small>
              <strong>Recommended format:</strong> <strong>MP4</strong> is the best choice - supports all devices, browsers, OS, and players. Small file size, fast loading, and ideal for websites and LMS platforms.
            </small>
            <br />
            <small>
              <strong>Size:</strong> max allowed 500MB, but for a 5‑minute intro we recommend keeping it much smaller (about 50–200MB) for faster upload.
            </small>
          </li>
          <li><strong>Thumbnail image:</strong> JPEG, PNG, GIF, WebP</li>
          <li><strong>Details:</strong> detailed description, requirements, what students will learn, target audience</li>
          <li><strong>Category/Subcategory + Language:</strong> choose the correct ones so students can find your course easily</li>
        </ul>
      </div>

      <div className="creation-steps">
        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faCircleInfo} />
          </div>
          <div className="step-details">
            <h4>Step 1: Create your course draft (Add New Course)</h4>
            <ul>
              <li><strong>Title</strong> (clear + simple)</li>
              <li><strong>Short description</strong> (max 20 words)</li>
              <li><strong>Detailed description</strong> (explain what this course covers)</li>
              <li><strong>Category</strong> and <strong>subcategory</strong></li>
              <li><strong>Language</strong> (or choose “Other” and type your language)</li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faVideo} />
          </div>
          <div className="step-details">
            <h4>Step 2: Upload introduction video + thumbnail</h4>
            <ul>
              <li><strong>Introduction video formats:</strong> MP4, AVI, MOV, WebM</li>
              <li><strong>Recommended format:</strong> <strong>MP4</strong> - supports all devices, browsers, OS, and players. Small file size, fast loading, and ideal for websites and LMS platforms.</li>
              <li><strong>Max duration:</strong> 5 minutes</li>
              <li><strong>Max size (allowed):</strong> 500MB</li>
              <li><strong>Recommended size:</strong> ~50–200MB (for faster upload)</li>
              <li><strong>Recommended quality:</strong> 720p or higher</li>
              <li><strong>Thumbnail formats:</strong> JPEG, PNG, GIF, WebP</li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faFileAlt} />
          </div>
          <div className="step-details">
            <h4>Step 3: Add learner-focused details</h4>
            <ul>
              <li><strong>Requirements:</strong> what students should know before starting</li>
              <li><strong>What students will learn:</strong> key outcomes</li>
              <li><strong>Target audience:</strong> who this course is for</li>
            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="step-details">
            <h4>Step 4: Submit to create a draft</h4>
            <ul>
              <li>Review your details</li>
              <li>Make sure the intro video plays correctly</li>
              <li>After submit, your course is saved as a <strong>Draft</strong></li>
            </ul>
          </div>
        </div>
        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faPenToSquare} />
          </div>
          <div className="step-details">
            <h4>Step 5: Edit your draft (Instructor Course Details)</h4>
            <ul>
              <li>Edit <strong>Intro</strong> (thumbnail/video/short description)</li>
              <li>Edit <strong>Overview</strong> (detailed description, requirements, learning outcomes, target audience, language)</li>
              <li>Set <strong>Price</strong> (actual price, offer price, currency, or mark as free)</li>
              <li>Add <strong>Sections</strong>, <strong>Lessons</strong>, and <strong>Attachments</strong></li>

            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faBook} />
          </div>
          <div className="step-details">
            <h4>Step 6: My Courses (Draft vs Published)</h4>
            <ul>
              <li>You can see all your courses (Draft and Published)</li>
              <li><strong>Draft courses</strong> can be deleted before publishing</li>
              <li><strong>Published courses</strong> cannot be deleted (but you can still edit them)</li>

            </ul>
          </div>
        </div>

        <div className="creation-step">
          <div className="step-icon">
            <FontAwesomeIcon icon={faBookOpen} />
          </div>
          <div className="step-details">
            <h4>Step 7: Publish and update changes</h4>
            <ul>
              <li><strong>Publish Requirements:</strong>
                <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
                  <li>Minimum 1 lesson</li>
                  <li>Minimum 30 minutes of total video content</li>
                  <li>Maximum 2GB total course size</li>
                </ul>
              </li>
              <li>If requirements are not met, the course will be saved as <strong>Draft</strong> and cannot be published</li>
              <li>When you publish, your course becomes visible to students</li>
              <li>If you edit a published course, your edits are saved as <strong>Draft changes</strong></li>
              <li>To show your latest edits to students, click <strong>Update</strong> (publish from draft)</li>
              <li>You can delete draft sections/lessons/attachments using the delete buttons</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContentCreation = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faVideo} /> Content Creation Tips</h2>

      <div className="tips-grid">
        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faVideo} />
          </div>
          <h4>Lesson Video Rules (important)</h4>
          <ul>
            <li><strong>Video formats:</strong> MP4, AVI, MOV, WebM</li>
            <li><strong>Recommended format:</strong> <strong>MP4</strong> - supports all devices, browsers, OS, and players. Small file size, fast loading, and ideal for websites and LMS platforms.</li>
            <li><strong>Minimum quality:</strong> 720p (required)</li>
            <li><strong>Course Size Options:</strong>
              <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
                <li><strong>Single video course:</strong> Maximum 1.5GB per lesson</li>
                <li><strong>Multiple lessons course:</strong> Maximum 2GB total for entire course</li>
              </ul>
            </li>
            <li><strong>Important:</strong> You can add your course as a single video (max 1.5GB) OR split it into multiple lessons (total of all lessons must not exceed 2GB)</li>
            <li>If your video is bigger than 1.5GB, you must <strong>split it into multiple lessons</strong> (total must not exceed 2GB)</li>
            <li>Use clear audio and good lighting</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <h4>Video Length (recommended)</h4>
          <ul>
            <li>Keep one lesson focused on one topic</li>
            <li>Break big topics into smaller lessons</li>
            <li>Add examples and practice steps</li>
            <li>Start each lesson with “what you’ll learn”</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faBook} />
          </div>
          <h4>Sections & Lessons (how to organize)</h4>
          <ul>
            <li><strong>Flexible:</strong> You can create 1 section with 1 lesson, or many sections with many lessons — it’s your choice.</li>
            <li>Create <strong>Sections</strong> for big chapters (example: Basics, Advanced, Projects)</li>
            <li>Add <strong>Lessons</strong> inside each section</li>
            <li>Use lesson names that explain the topic (not “Video 1”)</li>
            <li><strong>Recommended:</strong> split long content into shorter lessons (easier for students, easier to upload)</li>
            <li>At the end, add a summary lesson or next steps</li>
          </ul>
        </div>

        <div className="tip-card">
          <div className="tip-icon">
            <FontAwesomeIcon icon={faImage} />
          </div>
          <h4>Attachments (PDF, PPT, ZIP…)</h4>
          <ul>
            <li>Upload supporting files per lesson (notes, slides, resources)</li>
            <li><strong>Supported formats:</strong> PDF, DOC/DOCX, PPT/PPTX, TXT, ZIP, RAR</li>
            <li>Name attachments clearly (example: “Lesson 3 Notes”)</li>
            <li>Only share files you have the rights to distribute</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderBestPractices = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faStar} /> Best Practices</h2>

      <div className="practices-container">
        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faCircleInfo} />
          </div>
          <div className="practice-content">
            <h4>Follow the platform rules (saves time)</h4>
            <p>
              Keep the intro video within <strong>5 minutes</strong> and use supported formats (MP4/WebM/AVI).
              For lessons, use at least <strong>720p</strong> quality and keep each lesson focused on one topic.
            </p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faLightbulb} />
          </div>
          <div className="practice-content">
            <h4>Clear Learning Objectives</h4>
            <p>Define what students will learn by the end of your course. Make objectives specific, measurable, and achievable.</p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="practice-content">
            <h4>Know Your Audience</h4>
            <p>Tailor your content to your target audience's skill level, background, and learning goals.</p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faPlay} />
          </div>
          <div className="practice-content">
            <h4>Engaging Delivery</h4>
            <p>Use a conversational tone, include real-world examples, and maintain student engagement throughout.</p>
          </div>
        </div>

        <div className="practice-item">
          <div className="practice-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="practice-content">
            <h4>Quality Assurance</h4>
            <p>Review your content multiple times, test your videos, and ensure all information is accurate and up-to-date.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMarketing = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faChartLine} /> Marketing Your Course</h2>

      <div className="marketing-tips">
        <div className="marketing-card">
          <h4>Compelling Course Title</h4>
          <p>Create a title that clearly communicates the value and outcome students will get from your course.</p>
        </div>

        <div className="marketing-card">
          <h4>Detailed Description</h4>
          <p>Write a comprehensive description that highlights the benefits, includes testimonials if available, and addresses common pain points.</p>
        </div>

        <div className="marketing-card">
          <h4>Professional Thumbnail</h4>
          <p>Design an eye-catching thumbnail that represents your course content and appeals to your target audience.</p>
        </div>

        <div className="marketing-card">
          <h4>Social Media Promotion</h4>
          <p>Share your course on social media platforms, educational forums, and relevant online communities.</p>
        </div>
      </div>
    </div>
  );

  const renderEngagement = () => (
    <div className="guide-section">
      <h2><FontAwesomeIcon icon={faUsers} /> Student Engagement</h2>

      <div className="engagement-strategies">
        <div className="strategy-card">
          <div className="strategy-icon">
            <FontAwesomeIcon icon={faHandshake} />
          </div>
          <h4>Build Community</h4>
          <p>Encourage student interaction through discussions, Q&A sessions, and peer feedback opportunities.</p>
        </div>

        <div className="strategy-card">
          <div className="strategy-icon">
            <FontAwesomeIcon icon={faStar} />
          </div>
          <h4>Provide Feedback</h4>
          <p>Respond to student questions promptly and provide constructive feedback on their progress.</p>
        </div>

        <div className="strategy-card">
          <div className="strategy-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <h4>Track Progress</h4>
          <p>Monitor student engagement and performance to identify areas for improvement in your course.</p>
        </div>

        <div className="strategy-card">
          <div className="strategy-icon">
            <FontAwesomeIcon icon={faGraduationCap} />
          </div>
          <h4>Continuous Improvement</h4>
          <p>Regularly update your course content based on student feedback and industry changes.</p>
        </div>
      </div>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
    case 'getting-started':
      return renderGettingStarted();
    case 'course-creation':
      return renderCourseCreation();
    case 'content-creation':
      return renderContentCreation();
    case 'best-practices':
      return renderBestPractices();
    case 'marketing':
      return renderMarketing();
    case 'engagement':
      return renderEngagement();
    default:
      return renderGettingStarted();
    }
  };

  return (
    <div>
      <InstaTopbar />
      <div className="instructor-guide-container">

        <div className="guide-content">
          <div className="guide-sidebar">
            <h3>Guide Sections</h3>
            <ul className="section-nav">
              {sections.map((section) => (
                <li key={section.id}>
                  <button
                    className={`section-button ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(section.id)}
                  >
                    <FontAwesomeIcon icon={section.icon} />
                    {section.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="guide-main">
            <button
              className="close-guide-btn"
              onClick={() => navigate('/dashboard')}
              aria-label="Close Guide"
            >
                            ×
            </button>
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstructorGuide;
